import { HANDOFF_TARGET, RETURN_TARGET, type AgentSpec } from './agent-spec';

/**
 * Project Bobcat — agent templates: whole use-case agents, written once and
 * reused. Create builds a task from one whenever the task matches it, and has
 * the AI draft the rest; the Resource Library lists them too.
 *
 * Each follows the solution-building guide's patterns: ask, look up, show the
 * result, a way back when the lookup fails, and a person when anything breaks.
 * A lookup is a custom command: Create adds a placeholder script that says
 * it is not connected (`placeholder-scripts.ts`), and each template says
 * which one to replace before it works for real (`spec.connect`).
 *
 * Pure data, shared by the onboarding prompt (ids and descriptions) and the
 * client (the steps). It lives outside `server-only` because the client is a
 * consumer in its own right: the onboarding prompt is moving to the backend
 * (PSTUDIO-4629), which keeps its own copy, while the Resource Library and
 * agent creation still read this one. Keep the ids in step with the
 * backend's: an id the prompt names but this list lacks quietly becomes an
 * AI-drafted agent. One shared source is a backend request.
 */
export interface AgentTemplate {
  id: string;
  name: string;
  /** One line, for the Resource Library card and the onboarding prompt. */
  description: string;
  /** Matched against a task's name and example phrases, lower-cased. */
  match: RegExp;
  spec: AgentSpec;
}

const R = RETURN_TARGET;
const H = HANDOFF_TARGET;

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: 'order-status',
    name: 'Order Status',
    description:
      'Asks for the order number and email, looks the order up and reads back where it is.',
    match:
      /order status|where('s| is) (my|their|an?|the) (order|package)|track(ing)? (an? |my |the )?(order|package)|tracking number|has my order shipped/,
    spec: {
      steps: [
        {
          ref: 'ask-order',
          type: 'D',
          name: 'Ask for the order number',
          message: 'What’s your order number? It’s in your confirmation email.',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ORDER_NUMBER'],
          next: 'ask-email',
        },
        {
          ref: 'ask-email',
          type: 'D',
          name: 'Ask for the email on the order',
          message: 'And the email address on the order?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ORDER_EMAIL'],
          next: 'lookup',
        },
        {
          ref: 'lookup',
          type: 'A',
          name: 'Look up the order',
          command: 'OrderLookup',
          parameterInput: {
            order_number: 'ORDER_NUMBER',
            email: 'ORDER_EMAIL',
            save_status_to: 'ORDER_STATUS',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'show' },
            { value: 'false', next: 'not-found' },
            { value: 'error', next: H },
          ],
          variables: ['ORDER_STATUS'],
        },
        {
          ref: 'show',
          type: 'D',
          name: 'Show the order status',
          message: 'Your order {ORDER_NUMBER} is {ORDER_STATUS}.',
          next: R,
        },
        {
          ref: 'not-found',
          type: 'D',
          name: 'Order not found',
          message: 'I couldn’t find an order with that number and email.',
          answerRequired: true,
          buttons: [
            { label: 'Try again', next: 'ask-order' },
            { label: 'Talk to a person', next: H },
          ],
        },
      ],
      connect: [
        'Replace the placeholder OrderLookup script in Actions with one connected to your order system',
      ],
    },
  },
  {
    id: 'returns',
    name: 'Start a Return',
    description:
      'Asks for the order and the reason, starts the return and confirms what happens next.',
    match:
      /start (a |my )?return|return (an?|my|the|this) (item|order|product)|make a return|exchange (an?|my|the|this)|send (it|this|something) back/,
    spec: {
      steps: [
        {
          ref: 'ask-order',
          type: 'D',
          name: 'Ask for the order number',
          message: 'I can help with that. What’s the order number?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ORDER_NUMBER'],
          next: 'ask-reason',
        },
        {
          ref: 'ask-reason',
          type: 'D',
          name: 'Ask why',
          message: 'What’s the reason for the return?',
          answerRequired: true,
          variables: ['RETURN_REASON'],
          buttons: [
            { label: 'Wrong size', next: 'start' },
            { label: 'Damaged', next: 'start' },
            { label: 'Changed my mind', next: 'start' },
            { label: 'Something else', next: 'start' },
          ],
        },
        {
          ref: 'start',
          type: 'A',
          name: 'Start the return',
          command: 'StartReturn',
          parameterInput: {
            order_number: 'ORDER_NUMBER',
            reason: 'RETURN_REASON',
            save_label_url_to: 'RETURN_LABEL_URL',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'done' },
            { value: 'false', next: 'not-eligible' },
            { value: 'error', next: H },
          ],
          variables: ['RETURN_LABEL_URL'],
        },
        {
          ref: 'done',
          type: 'D',
          name: 'Confirm the return',
          message:
            'Your return is started. We’ve emailed you a prepaid label; drop the package off within 30 days.',
          next: R,
        },
        {
          ref: 'not-eligible',
          type: 'D',
          name: 'Not eligible',
          message: 'That order can’t be returned online.',
          answerRequired: true,
          buttons: [
            { label: 'Talk to a person', next: H },
            { label: 'Something else', next: R },
          ],
        },
      ],
      connect: [
        'Replace the placeholder StartReturn script in Actions with one connected to your returns system',
      ],
    },
  },
  {
    id: 'verify',
    name: 'Customer Verification',
    description:
      'Asks for an account or policy number and date of birth, checks them, and asks again or hands off when they don’t match.',
    match:
      /verif|confirm (who|identity)|authenticat|identity|log ?in to (my|their) account/,
    spec: {
      steps: [
        {
          ref: 'ask-id',
          type: 'D',
          name: 'Ask for the account number',
          message:
            'Before I look at your account, I need to confirm it’s you. What’s your account or policy number?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ACCOUNT_NUMBER'],
          next: 'ask-dob',
        },
        {
          ref: 'ask-dob',
          type: 'D',
          name: 'Ask for date of birth',
          message: 'Thanks. And your date of birth?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['DATE_OF_BIRTH'],
          next: 'check',
        },
        {
          ref: 'check',
          type: 'A',
          name: 'Verify the customer',
          command: 'VerifyCustomer',
          parameterInput: {
            account_number: 'ACCOUNT_NUMBER',
            date_of_birth: 'DATE_OF_BIRTH',
            save_verified_to: 'CUSTOMER_VERIFIED',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'verified' },
            { value: 'false', next: 'no-match' },
            { value: 'error', next: H },
          ],
          variables: ['CUSTOMER_VERIFIED'],
        },
        {
          ref: 'verified',
          type: 'D',
          name: 'Verified',
          message: 'Thanks, you’re verified. What can I help you with?',
          next: R,
        },
        {
          ref: 'no-match',
          type: 'D',
          name: 'Details don’t match',
          message: 'Those details don’t match our records.',
          answerRequired: true,
          buttons: [
            { label: 'Try again', next: 'ask-id' },
            { label: 'Talk to a person', next: H },
          ],
        },
      ],
      connect: [
        'Replace the placeholder VerifyCustomer script in Actions with one connected to your customer records',
      ],
    },
  },
  {
    id: 'claim-status',
    name: 'Claim Status',
    description:
      'Asks for the claim number, looks the claim up and reads back its status.',
    match:
      /claim (status|received|was received|has been received)|status of (a|my|their|the) claim|where('s| is) (my|their|the) claim|claim stands/,
    spec: {
      steps: [
        {
          ref: 'ask-claim',
          type: 'D',
          name: 'Ask for the claim number',
          message:
            'What’s your claim number? It’s on the email we sent when you filed.',
          answerRequired: true,
          nluDisabled: true,
          variables: ['CLAIM_NUMBER'],
          next: 'lookup',
        },
        {
          ref: 'lookup',
          type: 'A',
          name: 'Look up the claim',
          command: 'ClaimStatusLookup',
          parameterInput: {
            claim_number: 'CLAIM_NUMBER',
            save_status_to: 'CLAIM_STATUS',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'show' },
            { value: 'false', next: 'not-found' },
            { value: 'error', next: H },
          ],
          variables: ['CLAIM_STATUS'],
        },
        {
          ref: 'show',
          type: 'D',
          name: 'Show the claim status',
          message: 'Claim {CLAIM_NUMBER} is {CLAIM_STATUS}.',
          next: R,
        },
        {
          ref: 'not-found',
          type: 'D',
          name: 'Claim not found',
          message: 'I couldn’t find a claim with that number.',
          answerRequired: true,
          buttons: [
            { label: 'Try again', next: 'ask-claim' },
            { label: 'Talk to a person', next: H },
          ],
        },
      ],
      connect: [
        'Replace the placeholder ClaimStatusLookup script in Actions with one connected to your claims system',
      ],
    },
  },
  {
    id: 'id-card',
    name: 'Insurance ID Card',
    description:
      'Finds the policy’s current ID card and sends it as a link the customer can save.',
    match: /id card|proof of insurance|insurance card/,
    spec: {
      steps: [
        {
          ref: 'ask-policy',
          type: 'D',
          name: 'Ask for the policy number',
          message: 'What’s your policy number?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['POLICY_NUMBER'],
          next: 'find',
        },
        {
          ref: 'find',
          type: 'A',
          name: 'Find the ID card',
          command: 'GetIdCard',
          parameterInput: {
            policy_number: 'POLICY_NUMBER',
            save_url_to: 'ID_CARD_URL',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'send' },
            { value: 'false', next: H },
            { value: 'error', next: H },
          ],
          variables: ['ID_CARD_URL'],
        },
        {
          ref: 'send',
          type: 'D',
          name: 'Send the ID card',
          message: 'Here’s your insurance ID card: {ID_CARD_URL}',
          next: R,
        },
      ],
      connect: [
        'Replace the placeholder GetIdCard script in Actions with one connected to your policy system',
      ],
    },
  },
  {
    id: 'payment-due',
    name: 'Payment Due Date',
    description:
      'Looks up the next payment and reads back the amount and the date it’s due.',
    match:
      /payment due|next payment|when is my (bill|payment)|bill(ing)? (due|amount)|due date|how much (do i|i) owe|account balance/,
    spec: {
      steps: [
        {
          ref: 'ask-account',
          type: 'D',
          name: 'Ask for the account number',
          message: 'What’s your account or policy number?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ACCOUNT_NUMBER'],
          next: 'lookup',
        },
        {
          ref: 'lookup',
          type: 'A',
          name: 'Look up the next payment',
          command: 'BillingSummaryLookup',
          parameterInput: {
            account_number: 'ACCOUNT_NUMBER',
            save_amount_to: 'AMOUNT_DUE',
            save_date_to: 'NEXT_PAYMENT_DATE',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'show' },
            { value: 'false', next: H },
            { value: 'error', next: H },
          ],
          variables: ['AMOUNT_DUE', 'NEXT_PAYMENT_DATE'],
        },
        {
          ref: 'show',
          type: 'D',
          name: 'Show the payment due',
          message:
            'Your next payment of {AMOUNT_DUE} is due on {NEXT_PAYMENT_DATE}.',
          next: R,
        },
      ],
      connect: [
        'Replace the placeholder BillingSummaryLookup script in Actions with one connected to your billing system',
      ],
    },
  },

  // ---- From the 2026-10-14 ranking of real client solutions: the tasks the
  // most clients build, modelled on how they built them (AI Observability's
  // solution inventory). A command's `stub` is what its placeholder answers
  // until the real script is uploaded.
  {
    id: 'live-agent',
    name: 'Talk to a Person',
    description:
      'Checks whether agents are available for the skill, hands the chat over with a summary, and offers to take a message when the team is busy or closed.',
    match:
      /talk to (a |an )?(person|human|agent|representative)|live agent|speak (to|with) (a |an )?(person|human|agent)|customer service rep/,
    spec: {
      steps: [
        {
          ref: 'start',
          type: 'D',
          name: 'Check who is available',
          message: 'Let me check who’s available to help.',
          next: 'status',
        },
        {
          ref: 'status',
          type: 'A',
          name: 'Check agent availability',
          command: 'GetAgentStatus',
          parameterInput: {
            global_var: 'AGENT_DETAILS',
            skills: 'General',
            wkday_start_time: '8:00',
            wkday_end_time: '17:00',
            friday_start_time: '8:00',
            friday_end_time: '17:00',
            saturday_start_time: '',
            saturday_end_time: '',
            sunday_start_time: '',
            sunday_end_time: '',
            timezone: 'US/Eastern',
            holiday: '',
          },
          decisionVariable: 'agent_status',
          outcomes: [
            { value: 'online', next: H },
            { value: 'busy', next: 'busy' },
            { value: 'away', next: 'closed' },
            { value: 'offline', next: 'closed' },
            { value: 'error', next: H },
          ],
          variables: ['AGENT_DETAILS'],
          stub: 'online',
        },
        {
          ref: 'busy',
          type: 'D',
          name: 'Team is busy',
          message:
            'Our team is helping other customers right now, so the wait may be longer than usual.',
          answerRequired: true,
          buttons: [
            { label: 'Wait for someone', next: H },
            { label: 'Leave a message', next: 'ask-email' },
          ],
        },
        {
          ref: 'closed',
          type: 'D',
          name: 'Team is closed',
          message:
            'Our team is available weekdays from 8am to 5pm Eastern. Leave a message and we’ll get back to you.',
          answerRequired: true,
          buttons: [
            { label: 'Leave a message', next: 'ask-email' },
            { label: 'Something else', next: R },
          ],
        },
        {
          ref: 'ask-email',
          type: 'D',
          name: 'Ask for an email address',
          message: 'What email address should we reply to?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['CONTACT_EMAIL'],
          next: 'ask-message',
        },
        {
          ref: 'ask-message',
          type: 'D',
          name: 'Ask for the message',
          message: 'And what would you like help with?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['CONTACT_MESSAGE'],
          next: 'send',
        },
        {
          ref: 'send',
          type: 'A',
          name: 'Send the message to the team',
          command: 'SendEmail',
          parameterInput: {
            to_email: 'SUPPORT_EMAIL',
            subject: 'Chat message from a customer',
            body: 'Reply to {CONTACT_EMAIL}: {CONTACT_MESSAGE}',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'sent' },
            { value: 'false', next: 'not-sent' },
            { value: 'error', next: 'not-sent' },
          ],
          variables: ['CONTACT_EMAIL', 'CONTACT_MESSAGE'],
        },
        {
          ref: 'sent',
          type: 'D',
          name: 'Message sent',
          message:
            'Thanks. We’ve passed your message to the team and they’ll reply to {CONTACT_EMAIL}.',
          next: R,
        },
        {
          ref: 'not-sent',
          type: 'D',
          name: 'Message not sent',
          message:
            'Sorry, I couldn’t send your message just now. Please try again later.',
          next: R,
        },
      ],
      connect: [
        'Set the team’s skill and hours in Check agent availability, and replace the placeholder GetAgentStatus script in Actions',
        'Replace the placeholder SendEmail script in Actions, and put the support address in the solution’s config',
      ],
    },
  },
  {
    id: 'csat-survey',
    name: 'Rate Your Experience',
    description:
      'Asks the customer to rate the chat from 1 to 5, invites a comment, saves the answer and thanks them.',
    match:
      /survey|csat|rate (the |your |our |this )?(chat|experience|conversation)|feedback/,
    spec: {
      steps: [
        {
          ref: 'rate',
          type: 'D',
          name: 'Ask for a rating',
          message: 'Before you go, how would you rate this chat?',
          answerRequired: true,
          variables: ['CSAT_SCORE'],
          buttons: [
            { label: '5 – Great', next: 'comment' },
            { label: '4', next: 'comment' },
            { label: '3', next: 'comment' },
            { label: '2', next: 'comment' },
            { label: '1 – Poor', next: 'comment' },
          ],
        },
        {
          ref: 'comment',
          type: 'D',
          name: 'Ask for a comment',
          message: 'Thanks! Anything we could do better? You can type it here.',
          answerRequired: true,
          nluDisabled: true,
          variables: ['CSAT_COMMENT'],
          next: 'save',
        },
        {
          ref: 'save',
          type: 'A',
          name: 'Save the survey answer',
          command: 'SaveSurveyResponse',
          parameterInput: { score: 'CSAT_SCORE', comment: 'CSAT_COMMENT' },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'thanks' },
            { value: 'false', next: 'thanks' },
            { value: 'error', next: 'thanks' },
          ],
          variables: ['CSAT_SCORE', 'CSAT_COMMENT'],
        },
        {
          ref: 'thanks',
          type: 'D',
          name: 'Thank the customer',
          message: 'Thank you for the feedback. Have a great day!',
          next: R,
        },
      ],
      connect: [
        'Replace the placeholder SaveSurveyResponse script in Actions with one that writes to your reporting (e.g. PostEventToBQ)',
      ],
    },
  },
  {
    id: 'appointment',
    name: 'Book an Appointment',
    description:
      'Asks for a day and a time of day, books it, and offers another time or a person when the slot is not free.',
    match:
      /appointment|schedul|reschedul|book (a |an )?(visit|service|consult|tour|call)/,
    spec: {
      steps: [
        {
          ref: 'ask-day',
          type: 'D',
          name: 'Ask for a day',
          message: 'What day works best for you?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['APPOINTMENT_DATE'],
          next: 'ask-time',
        },
        {
          ref: 'ask-time',
          type: 'D',
          name: 'Ask for a time of day',
          message: 'And what time of day?',
          answerRequired: true,
          variables: ['APPOINTMENT_TIME'],
          buttons: [
            { label: 'Morning', next: 'book' },
            { label: 'Afternoon', next: 'book' },
            { label: 'Evening', next: 'book' },
          ],
        },
        {
          ref: 'book',
          type: 'A',
          name: 'Book the appointment',
          command: 'BookAppointment',
          parameterInput: {
            date: 'APPOINTMENT_DATE',
            time_of_day: 'APPOINTMENT_TIME',
            save_confirmation_to: 'APPOINTMENT_CONFIRMATION',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'booked' },
            { value: 'false', next: 'not-free' },
            { value: 'error', next: H },
          ],
          variables: ['APPOINTMENT_CONFIRMATION'],
        },
        {
          ref: 'booked',
          type: 'D',
          name: 'Confirm the booking',
          message:
            'You’re booked for {APPOINTMENT_DATE}, in the {APPOINTMENT_TIME}. Your confirmation number is {APPOINTMENT_CONFIRMATION}.',
          next: R,
        },
        {
          ref: 'not-free',
          type: 'D',
          name: 'Time not available',
          message: 'That time isn’t available.',
          answerRequired: true,
          buttons: [
            { label: 'Pick another time', next: 'ask-day' },
            { label: 'Talk to a person', next: H },
          ],
        },
      ],
      connect: [
        'Replace the placeholder BookAppointment script in Actions with one connected to your scheduling system',
      ],
    },
  },
  {
    id: 'sign-in-help',
    name: 'Sign-in Help',
    description:
      'Finds out what is wrong with signing in, checks the email on the account and sends a password reset link.',
    match:
      /password|log ?in (help|issue|trouble|problem)|sign ?in (help|issue|trouble|problem)|can'?t (log|sign) ?in|locked out/,
    spec: {
      steps: [
        {
          ref: 'what',
          type: 'D',
          name: 'Ask what is wrong',
          message: 'Sorry you’re having trouble signing in. What’s going on?',
          answerRequired: true,
          buttons: [
            { label: 'I forgot my password', next: 'ask-email' },
            { label: 'My account is locked', next: 'ask-email' },
            { label: 'Something else', next: H },
          ],
        },
        {
          ref: 'ask-email',
          type: 'D',
          name: 'Ask for the account email',
          message: 'What’s the email address on your account?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ACCOUNT_EMAIL'],
          next: 'check-email',
        },
        {
          ref: 'check-email',
          type: 'A',
          name: 'Check the email address',
          command: 'ValidateEmail',
          parameterInput: { global_vars: 'ACCOUNT_EMAIL' },
          decisionVariable: 'valid',
          outcomes: [
            { value: 'true', next: 'send' },
            { value: 'false', next: 'bad-email' },
            { value: 'error', next: H },
          ],
          variables: ['ACCOUNT_EMAIL'],
          stub: 'true',
        },
        {
          ref: 'bad-email',
          type: 'D',
          name: 'Email not valid',
          message: 'That doesn’t look like an email address.',
          answerRequired: true,
          buttons: [
            { label: 'Try again', next: 'ask-email' },
            { label: 'Talk to a person', next: H },
          ],
        },
        {
          ref: 'send',
          type: 'A',
          name: 'Send a reset link',
          command: 'SendPasswordReset',
          parameterInput: { email: 'ACCOUNT_EMAIL' },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'sent' },
            { value: 'false', next: 'not-sent' },
            { value: 'error', next: H },
          ],
          variables: ['ACCOUNT_EMAIL'],
        },
        {
          ref: 'sent',
          type: 'D',
          name: 'Reset link sent',
          message:
            'We’ve emailed a reset link to {ACCOUNT_EMAIL}. It expires in 30 minutes, so use it soon.',
          next: R,
        },
        {
          ref: 'not-sent',
          type: 'D',
          name: 'Reset link not sent',
          message: 'I couldn’t send a reset link to that address.',
          answerRequired: true,
          buttons: [
            { label: 'Try another email', next: 'ask-email' },
            { label: 'Talk to a person', next: H },
          ],
        },
      ],
      connect: [
        'Replace the placeholder SendPasswordReset script in Actions with one connected to your sign-in system, and the ValidateEmail placeholder with your validator',
      ],
    },
  },
  {
    id: 'update-contact',
    name: 'Update Contact Details',
    description:
      'Updates a phone number, email or mailing address, checking each one before it is saved.',
    match:
      /update (my |your |the )?(contact|address|phone|email)|change (of |my |your )?(address|phone|email)|address change|contact (details|info)/,
    spec: {
      steps: [
        {
          ref: 'what',
          type: 'D',
          name: 'Ask what to update',
          message: 'What would you like to update?',
          answerRequired: true,
          buttons: [
            { label: 'Phone number', next: 'ask-phone' },
            { label: 'Email address', next: 'ask-email' },
            { label: 'Mailing address', next: 'ask-address' },
          ],
        },
        {
          ref: 'ask-phone',
          type: 'D',
          name: 'Ask for the new phone number',
          message: 'What’s the new phone number?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['NEW_PHONE'],
          next: 'check-phone',
        },
        {
          ref: 'check-phone',
          type: 'A',
          name: 'Check the phone number',
          command: 'ValidatePhoneAndReturnStripped',
          parameterInput: {
            original_phone_number: 'NEW_PHONE',
            stripped_phone_number: 'NEW_PHONE',
          },
          decisionVariable: 'valid',
          outcomes: [
            { value: 'true', next: 'save' },
            { value: 'false', next: 'invalid' },
            { value: 'error', next: H },
          ],
          variables: ['NEW_PHONE'],
          stub: 'true',
        },
        {
          ref: 'ask-email',
          type: 'D',
          name: 'Ask for the new email',
          message: 'What’s the new email address?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['NEW_EMAIL'],
          next: 'check-email',
        },
        {
          ref: 'check-email',
          type: 'A',
          name: 'Check the email address',
          command: 'ValidateEmail',
          parameterInput: { global_vars: 'NEW_EMAIL' },
          decisionVariable: 'valid',
          outcomes: [
            { value: 'true', next: 'save' },
            { value: 'false', next: 'invalid' },
            { value: 'error', next: H },
          ],
          variables: ['NEW_EMAIL'],
          stub: 'true',
        },
        {
          ref: 'ask-address',
          type: 'D',
          name: 'Ask for the new address',
          message: 'What’s the new mailing address, including the ZIP code?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['ADDRESS_REQUEST'],
          next: 'check-address',
        },
        {
          ref: 'check-address',
          type: 'A',
          name: 'Check the address',
          command: 'ValidateAddress',
          parameterInput: { global_vars: 'ADDRESS_REQUEST' },
          decisionVariable: 'valid',
          outcomes: [
            { value: 'true', next: 'save' },
            { value: 'maybe', next: 'save' },
            { value: 'false', next: 'invalid' },
            { value: 'error', next: H },
          ],
          variables: ['ADDRESS_REQUEST'],
          stub: 'true',
        },
        {
          ref: 'invalid',
          type: 'D',
          name: 'Detail not valid',
          message: 'That doesn’t look quite right.',
          answerRequired: true,
          buttons: [
            { label: 'Try again', next: 'what' },
            { label: 'Talk to a person', next: H },
          ],
        },
        {
          ref: 'save',
          type: 'A',
          name: 'Save the new details',
          command: 'UpdateContactDetails',
          parameterInput: {
            phone: 'NEW_PHONE',
            email: 'NEW_EMAIL',
            address: 'ADDRESS_REQUEST',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'saved' },
            { value: 'false', next: H },
            { value: 'error', next: H },
          ],
          variables: ['NEW_PHONE', 'NEW_EMAIL', 'ADDRESS_REQUEST'],
        },
        {
          ref: 'saved',
          type: 'D',
          name: 'Details updated',
          message: 'Done. Your contact details are updated.',
          next: R,
        },
      ],
      connect: [
        'Replace the placeholder UpdateContactDetails script in Actions with one connected to your customer records, and the three validator placeholders with your own',
      ],
    },
  },
  {
    id: 'send-link',
    name: 'Send a Link',
    description:
      'Sends the customer a link by text or email, and shows it in the chat if it cannot be sent.',
    match:
      /send (me |you )?(a |the )?link|text (me|you) (a |the )?link|email (me|you) (a |the )?link/,
    spec: {
      steps: [
        {
          ref: 'how',
          type: 'D',
          name: 'Ask how to send it',
          message: 'I can send you the link. Text or email?',
          answerRequired: true,
          buttons: [
            { label: 'Text message', next: 'ask-phone' },
            { label: 'Email', next: 'ask-email' },
          ],
        },
        {
          ref: 'ask-phone',
          type: 'D',
          name: 'Ask for a mobile number',
          message: 'What mobile number should I text?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['LINK_PHONE'],
          next: 'check-phone',
        },
        {
          ref: 'check-phone',
          type: 'A',
          name: 'Check the mobile number',
          command: 'ValidatePhoneAndReturnStripped',
          parameterInput: {
            original_phone_number: 'LINK_PHONE',
            stripped_phone_number: 'LINK_PHONE',
          },
          decisionVariable: 'valid',
          outcomes: [
            { value: 'true', next: 'text' },
            { value: 'false', next: 'invalid' },
            { value: 'error', next: 'not-sent' },
          ],
          variables: ['LINK_PHONE'],
          stub: 'true',
        },
        {
          ref: 'text',
          type: 'A',
          name: 'Text the link',
          command: 'SendTwilioMessage',
          parameterInput: {
            to_number: 'LINK_PHONE',
            message: 'Here’s the link you asked for: {LINK_URL}',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'sent' },
            { value: 'false', next: 'not-sent' },
            { value: 'error', next: 'not-sent' },
          ],
          variables: ['LINK_PHONE', 'LINK_URL'],
        },
        {
          ref: 'ask-email',
          type: 'D',
          name: 'Ask for an email address',
          message: 'What email address should I send it to?',
          answerRequired: true,
          nluDisabled: true,
          variables: ['LINK_EMAIL'],
          next: 'check-email',
        },
        {
          ref: 'check-email',
          type: 'A',
          name: 'Check the email address',
          command: 'ValidateEmail',
          parameterInput: { global_vars: 'LINK_EMAIL' },
          decisionVariable: 'valid',
          outcomes: [
            { value: 'true', next: 'email' },
            { value: 'false', next: 'invalid' },
            { value: 'error', next: 'not-sent' },
          ],
          variables: ['LINK_EMAIL'],
          stub: 'true',
        },
        {
          ref: 'email',
          type: 'A',
          name: 'Email the link',
          command: 'SendEmail',
          parameterInput: {
            to_email: 'LINK_EMAIL',
            subject: 'Your link',
            body: 'Here’s the link you asked for: {LINK_URL}',
          },
          decisionVariable: 'success',
          outcomes: [
            { value: 'true', next: 'sent' },
            { value: 'false', next: 'not-sent' },
            { value: 'error', next: 'not-sent' },
          ],
          variables: ['LINK_EMAIL', 'LINK_URL'],
        },
        {
          ref: 'invalid',
          type: 'D',
          name: 'Contact not valid',
          message: 'That doesn’t look right.',
          answerRequired: true,
          buttons: [
            { label: 'Try again', next: 'how' },
            { label: 'Just show it here', next: 'not-sent' },
          ],
        },
        {
          ref: 'sent',
          type: 'D',
          name: 'Link sent',
          message: 'Sent! It should arrive in a minute.',
          next: R,
        },
        {
          ref: 'not-sent',
          type: 'D',
          name: 'Show the link in chat',
          message: 'Here’s the link: {LINK_URL}',
          next: R,
        },
      ],
      connect: [
        'Set LINK_URL before sending a customer here, and replace the placeholder SendTwilioMessage and SendEmail scripts in Actions (credentials go in the solution’s config, not the node)',
      ],
    },
  },
];

/**
 * The template a task matches, by its name and example phrases. The
 * onboarding model may name one itself (`TalkDraft.tasks[].template`); this is
 * the check for when it didn't.
 */
/** A task about questions or policy is the Knowledge Agent's kind of work
 *  ("Shipping and returns questions"), never a template's flow. */
const ANSWERS_QUESTIONS =
  /question|\bpolic(y|ies)\b|\bfaqs?\b|\binfo\b|information/;

export const matchAgentTemplate = (
  name: string,
  phrases: string[] = []
): AgentTemplate | null => {
  if (ANSWERS_QUESTIONS.test(name.toLowerCase())) return null;
  const text = [name, ...phrases].join(' \n ').toLowerCase();
  return AGENT_TEMPLATES.find((t) => t.match.test(text)) ?? null;
};

export const agentTemplateById = (id: string | undefined) =>
  id ? (AGENT_TEMPLATES.find((t) => t.id === id) ?? null) : null;
